import { createContext, useContext, type ReactNode } from "react"

/** Public UI config from GET /api/config — branding comes from the agent's config.yaml web_config. */
export interface AppConfig {
  title: string
  description: string
  auth_mode: "msal" | "platform"
  dev_memory_stores: boolean
}

export const DEFAULT_CONFIG: AppConfig = {
  title: "celver Copilot",
  description: "",
  auth_mode: "msal",
  dev_memory_stores: false,
}

const AppConfigContext = createContext<AppConfig>(DEFAULT_CONFIG)

export function AppConfigProvider({ value, children }: { value: AppConfig; children: ReactNode }) {
  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>
}

export function useAppConfig(): AppConfig {
  return useContext(AppConfigContext)
}

/**
 * Fetch the config. Falls back to the sharepoint-bot defaults when the endpoint is
 * unreachable (e.g. `vite dev` without the backend), which keeps the existing MSAL flow.
 */
export async function fetchAppConfig(): Promise<AppConfig> {
  try {
    const response = await fetch("/api/config")
    if (!response.ok) return DEFAULT_CONFIG
    const body = (await response.json()) as Partial<AppConfig>
    return {
      title: body.title || DEFAULT_CONFIG.title,
      description: body.description ?? "",
      auth_mode: body.auth_mode === "platform" ? "platform" : "msal",
      dev_memory_stores: body.dev_memory_stores === true,
    }
  } catch {
    return DEFAULT_CONFIG
  }
}

/** "celver Copilot" -> ["celver", "Copilot"]; "My Bot" -> ["celver", "My Bot"]. Keeps the CI wordmark shape. */
export function wordmarkParts(title: string): [string, string] {
  const rest = title.replace(/^celver\s+/i, "").trim()
  return ["celver", rest || title]
}
